import { z, type ZodType } from 'zod';
import type { ServicesApiAsset, ServicesApiAssetUser } from '@/services/api/services-api';
import { defineEntity } from '../shared/define-entity';
import { epochSecondsToDate } from '../shared/zod-helpers';

/**
 * Asset entity — workspace-scoped files and folders.
 *
 * Assets live under `/v1/environments/:environment_id/assets` and
 * represent images, documents, and folders in the workspace's asset
 * library. The list endpoint returns a flat page of assets within a
 * folder, plus `parents` (the breadcrumb chain to root).
 *
 * **`id` is a `string`** — the Go API sends and expects string IDs.
 * We keep them as strings throughout the codebase to avoid pointless
 * string-to-number-to-string round-trips.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

type AssetUser = ServicesApiAssetUser;

export type Asset = Omit<ServicesApiAsset, 'created_at' | 'updated_at' | 'created_by' | 'updated_by'> & {
  created_by: AssetUser;
  updated_by: AssetUser;
  created_at: Date;
  updated_at: Date;
};

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

/**
 * The Go API returns `id` as a string and `parent_id` as a number (or null).
 * Coerce both to strings so the rest of the codebase works with string IDs.
 */
const nullableStringCoerce = z
  .union([z.string(), z.number().transform(String), z.null()])
  .transform((v) => (v === null ? null : String(v)));

const AssetFromApi = z.looseObject({
  id: z.coerce.string(),
  parent_id: nullableStringCoerce,
  created_at: epochSecondsToDate,
  updated_at: epochSecondsToDate,
}) as unknown as ZodType<Asset, unknown>;

/* ------------------------------------------------------------------------- */
/* List params                                                                */
/* ------------------------------------------------------------------------- */

export interface AssetListParams {
  folder_id?: string;
  page?: string;
  search?: string;
  sort?: string;
  order?: string;
  limit?: string | number;
}

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const asset = defineEntity<Asset, never, AssetListParams, undefined, 'workspace'>({
  name: 'asset',
  scope: 'workspace',
  pk: (a) => a.id,
  schema: { read: AssetFromApi },
});

export const assetKeys = asset.keys;

export function parseAsset(raw: ServicesApiAsset): Asset {
  return asset.parse(raw);
}
